/* eslint-disable import/no-named-as-default-member */
/* eslint-disable import/no-duplicates */
import firebaseApp from 'firebase/app';
import 'firebase/auth';
import 'firebase/firestore';
import 'firebase/functions';
import { isStudent, isTeacher, ROLES } from '../constants/roles';
import { Classroom, ClassroomStudent, TeacherClassroom } from '../types/classroom';
import { mapTokenToRole, TokenDocument, TOKEN_TYPE } from '../types/tokenDocument';
import { ITSUser, Name } from '../types/user';
import { ActiveLessons } from '../types/lesson';
import { JWT } from './lti';
import { APP_CFG } from '../utils/appcfg';
import { SavedUserState, ScoredOption } from 'its-game/src/utils';
import { RawEvalPageData, RawPageData, Skill } from '../../../its-pages/src';
import { processEvent } from '../utils/teacher-cards';
import { CardIdToSelectionMap, FirebaseCardType, FirebaseRawCardData, FirebaseUserSkillCardEvents, StudentResult } from '../types/cards';
import { CardSettingsPayload, DashboardLoadPayload } from '../redux/actions/teacher-ui-actions';
import { SavedModuleCompletionInfo } from '../types/module-completion';
import { BugReport, BugReportStatus, SelfEvalResponse, SelfEvalStatus } from '../types/admin';

type LessonDescriptor = {
  classroomToken: string;
  lessonKey: string;

  /**
   * Can be either just the module name (ie Empathy) or combined module name with a version (Empathy-V2)
   */
  moduleKey: string;
};

const prodConfig = {
  apiKey: 'AIzaSyCm0KHSLzl_iurr5m1pMhIvBJ_RSWyn8tk',
  authDomain: 'copilot-2021.firebaseapp.com',
  databaseURL: 'https://copilot-2021.firebaseio.com',
  projectId: 'copilot-2021',
  storageBucket: 'copilot-2021.appspot.com',
  messagingSenderId: '239279688093',
  appId: '1:239279688093:web:669c4768d31d1f47f68774',
  measurementId: 'G-5VYF9YE1GD',
};

const devConfig = {
  apiKey: 'AIzaSyBQWQYP4JjyhQZCC5sUCDarJo15nhxkUlE',
  authDomain: 'rictest-55650.firebaseapp.com',
  databaseURL: 'https://rictest-55650.firebaseio.com',
  projectId: 'rictest-55650',
  storageBucket: 'rictest-55650.appspot.com',
  messagingSenderId: '770999499883',
  appId: '1:770999499883:web:fa52e651ea3d89f417052e',
  measurementId: 'G-2C6L02KJRY',
};

export class NoTokenError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = 'No Token';
  }
}

export class NoURLError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = 'No Url';
  }
}

export class DocumentExistsError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = 'Document Exists';
  }
}

export type Token = string;
const config = APP_CFG.isProd ? prodConfig : devConfig;

type IssueCardDetails = {
  tagId: string;
  questionType: string;
};
type ConfusionCardDetails = {
  tagAId: string;
  tagBId: string;
};
type CardDetails = IssueCardDetails | ConfusionCardDetails | { questionType: string };
type UserDetails = {
  uid: string;
  correct: boolean;
  problemLevel?: number;
  questionType?: string;
  ts: number;
};

type CardStat = {
  cardId: string
  cardType: string;
  details: CardDetails;
  userInstanceDetails: UserDetails;
};

export class Firebase {
  public auth: firebaseApp.auth.Auth;
  private db: firebaseApp.firestore.Firestore;
  private functions: firebaseApp.functions.Functions;
  private addTeacherRoleFn: firebaseApp.functions.HttpsCallable;
  private addStudentRoleFn: firebaseApp.functions.HttpsCallable;
  private addStudentToClassFn: firebaseApp.functions.HttpsCallable;
  private getAssignmentsFn: firebaseApp.functions.HttpsCallable;

  constructor() {
    firebaseApp.initializeApp(config);
    this.auth = firebaseApp.auth();
    this.db = firebaseApp.firestore();
    this.functions = firebaseApp.functions();
    this.addTeacherRoleFn = this.functions.httpsCallable('addTeacherRoleV2');
    this.addStudentRoleFn = this.functions.httpsCallable('addStudentRoleV2');
    this.addStudentToClassFn =
      this.functions.httpsCallable('addStudentToClassV2');
    this.getAssignmentsFn = this.functions.httpsCallable('getAssignmentsV2');
  }

  public async doCreateUserWithEmailAndPassword(email: string, pw: string) {
    return await this.auth.createUserWithEmailAndPassword(email, pw);
  }

  async doSignInWithJWT(jwt: JWT) {
    return await this.auth.signInWithCustomToken(jwt);
  }

  public async doSignInWithEmailAndPassword(email: string, password: string) {
    return await this.auth.signInWithEmailAndPassword(email, password);
  }

  public async doSignInWithPopup(providerName: string) {
    let provider: firebaseApp.auth.AuthProvider | null = null;

    switch (providerName) {
      case 'google':
        provider = new firebaseApp.auth.GoogleAuthProvider();
        break;
      case 'apple': {
        const oProvider = new firebaseApp.auth.OAuthProvider('apple.com');
        // provider.addScope("email");
        oProvider.addScope('name');
        provider = oProvider;
        break;
      }
      case 'microsoft':
        provider = new firebaseApp.auth.OAuthProvider('microsoft.com');
        (provider as firebaseApp.auth.OAuthProvider).setCustomParameters({
          tenant: 'f6b6dd5b-f02f-441a-99a0-162ac5060bd2',
        });
        break;
      case 'twitter':
        provider = new firebaseApp.auth.TwitterAuthProvider();
        break;
      case 'facebook':
        provider = new firebaseApp.auth.FacebookAuthProvider();
        break;
      default:
        console.log('unknown provider ', providerName);
        return;
    }

    return await this.auth.signInWithPopup(provider);
  }

  public async setRole(email: string, role: ROLES) {
    const addRole = isTeacher(role)
      ? this.addTeacherRoleFn
      : this.addStudentRoleFn;

    await addRole({ email });
  }

  public async doSignOut() {
    return await this.auth.signOut();
  }

  public async doPasswordReset(email: string) {
    return await this.auth.sendPasswordResetEmail(email);
  }

  public async doPasswordUpdate(password: string) {
    return await this.auth.currentUser?.updatePassword(password);
  }

  /**
   * Make sure the canvas classroom information is exists.
   * Other parts of the app require this information to be present
   * 
   * @param param0 
   * @returns 
   */
  public async ensureLTIClassroom({
    user,
    teacherClassroom,
  }: {
    user: ITSUser;
    teacherClassroom: TeacherClassroom;
  }): Promise<boolean> {
    const teacherClassroomRef = this.teacherClassroom(teacherClassroom.token);
    const userRef = this.user(user.uid);

    try {
      await this.db.runTransaction(async (transaction) => {
        const teacherClassroomDocument = await transaction.get(
          teacherClassroomRef
        );
        const exists = teacherClassroomDocument.exists;

        if (!exists) {
          // add class to classes collection
          transaction.set(teacherClassroomRef, teacherClassroom, {
            merge: false,
          });
        }

        user.classrooms = { ...user.classrooms };
        // add class to user classes array
        user.classrooms[teacherClassroom.token] = teacherClassroom;
        transaction.set(userRef, user, { merge: false });
      });
      return true;
    } catch {
      return false;
    }
  }

  public async ensureLTIStudent(
    uid: string,
    username: string,
    name: Name,
    classroom: Classroom
  ): Promise<ITSUser | undefined> {
    const classToken = classroom.token;
    const user: ITSUser = {
      uid,
      username,
      name,
      email: '',
      classrooms: { [classToken]: classroom },
    };

    const userRef = this.user(uid);
    const teacherClassroomRef = this.teacherClassroom(classToken);

    try {
      this.db.runTransaction(async (transaction) => {
        const classroomDoc = await transaction.get(teacherClassroomRef);
        const classroomExists = classroomDoc.exists;

        transaction.set(userRef, user, { merge: true });

        if (!classroomExists) {
          // add class to classes collection
          const teacherClassroom: TeacherClassroom = {
            ...classroom,
            students: {},
            activeLessons: { ITS2021: [] },
          };
          transaction.set(teacherClassroomRef, teacherClassroom, {
            merge: true,
          });
        }

        this.addStudentToClass(uid, classToken);
      });
      return user;
    } catch {}
  }

  public async ensureLTITeacher(
    uid: string,
    username: string,
    name: Name,
    classroom: Classroom
  ): Promise<ITSUser | undefined> {
    const classToken = classroom.token;
    const user: ITSUser = {
      uid,
      username,
      name,
      email: '',
      classrooms: { [classToken]: classroom },
    };

    const userRef = this.user(uid);
    const teacherClassroomRef = this.teacherClassroom(classToken);

    try {
      this.db.runTransaction(async (transaction) => {
        const classroomDoc = await transaction.get(teacherClassroomRef);
        const classroomExists = classroomDoc.exists;

        transaction.set(userRef, user, { merge: true });

        if (classroomExists) {
          return;
        }
        // add class to classes collection
        const teacherClassroom: TeacherClassroom = {
          ...classroom,
          students: {},
          activeLessons: { ITS2021: [] },
        };
        transaction.set(teacherClassroomRef, teacherClassroom, {
          merge: true,
        });
      });
      return user;
    } catch {}
  }

  public async createUser({
    uid,
    username,
    createToken,
    email,
    name,
  }: {
    uid: string;
    username: string;
    createToken: string;
    email: string;
    name: Name;
  }): Promise<ROLES> {
    const tokenQuerySnapshot = await this.tokenDocs()
      .where('token', '==', createToken)
      .get();

    if (tokenQuerySnapshot.empty) {
      throw new NoTokenError('Given token not registered');
    }

    // should only be one document with the given token
    const tokenDoc =
      tokenQuerySnapshot.docs[0].data() as unknown as TokenDocument;

    const role = mapTokenToRole(tokenDoc);
    const addRoleFn = isStudent(role)
      ? this.addStudentRoleFn
      : this.addTeacherRoleFn;

    const user: ITSUser = {
      uid,
      username,
      name,
      email,
      classrooms: {},
    };

    const userDocReference = this.user(uid);

    try {
      await this.db.runTransaction(async (transaction) => {
        transaction.set(userDocReference, user, { merge: true });

        await addRoleFn({ email });
        if (isStudent(role)) {
          this.addStudentToClass(uid, createToken);
        }
      });

      return role;
    } catch {}
    return role;
  }

  
  /**
   * Save completion info for this user for a module
   * 
   * @param uid 
   * @param moduleName 
   * @param classCode 
   * @param data 
   */
  public async saveModuleCompletionInfo(
    uid: string,
    moduleName: string,
    classCode: string,
    data: SavedModuleCompletionInfo
  ) {
    const document = await this.getModuleCompletionDocRef(uid, classCode);
    const options = (document.exists) ?  { merge: true } : {};
    document.ref.set({ [moduleName]: data}, options);
  }

  /**
   * 
   * @param uid 
   * @param moduleName 
   * @param classCode 
   * @returns undefined if no completion data found for that module
   */
  public async getModuleCompletionInfo(
    uid: string,
    moduleName: string,
    classCode: string
  ): Promise<SavedModuleCompletionInfo | undefined> {
    const document = await this.getModuleCompletionDocRef(uid, classCode);
    if (document.exists) {
      return document.get(moduleName);
    }
    return undefined;
  }

  private async getModuleCompletionDocRef(uid: string, classCode: string) {
    const docRef = this.db.doc(`userClassData/${classCode}/moduleCompletion/${uid}`);
    return docRef.get();
  }

  public async saveModuleVersion(classCode: string, moduleName: string, version: string) {
    const document = await this.getModuleVersionDocRef(classCode);
    const options = (document.exists) ?  { merge: true } : {};
    document.ref.set({ [moduleName]: version }, options);
  }

  public async getModuleVersions(classCode: string) {
    const document = await this.getModuleVersionDocRef(classCode);
    if (document.exists) {
      return document.data();
    }
  }

  private async getModuleVersionDocRef(classCode: string) {
    const docRef = this.db.doc(`userClassData/${classCode}/moduleSettings/versions`);
    return docRef.get();
  }

  public async updateUser(
    user: ITSUser,
    data: Partial<ITSUser>
  ): Promise<void> {
    const userDocReference = this.user(user.uid);

    await userDocReference.set(data, { merge: true });
  }

  /**
   * 
   * @param uid 
   * @param moduleDesc composite type (note that moduleKey can contain a version portion, and not just the
   * module name)
   * @param lessonState 
   */
  public async saveUserState(
    uid: string,
    moduleDesc: LessonDescriptor,
    lessonState: SavedUserState
  ) {
    const { classroomToken, lessonKey, moduleKey } = moduleDesc;

    const userDocReference = this.user(uid);
    const classroomDocRef = this.teacherClassroom(classroomToken);

    const userUpdateDiff = {
      gameState: {
        [classroomToken]: {
          [lessonKey]: {
            [moduleKey]: JSON.stringify(lessonState),
          },
        },
      },
    };

    const {
      progression: { progress, cumulativeTime },
    } = lessonState.moduleState;

    /*
     * Not sure why the type is just a plain object here
     * instead of Immutable.Map (per progression reducer)
     */
    const problemsLog = lessonState.moduleState.progression
      .problemsDone as unknown as Record<string, ScoredOption[]>;

    const problemsDone = Object.values(problemsLog).reduce((sum, arr) => {
      return sum + arr.length;
    }, 0);

    const studentState: Partial<ClassroomStudent> = {
      stats: {
        [lessonKey]: {
          [moduleKey]: {
            progress,
            cumulativeTime,
            problemsDone
          },
        },
      },
    };

    // Update classroom document with student information, also automatically shift over the end date of
    // the class (want loki to include this latest result)
    const classroomUpdateDiff= {
      students: { [uid]: studentState, },
      endTsMs: Date.now() + 1000
    };

    await Promise.all([
      userDocReference.set(userUpdateDiff, { merge: true }),
      classroomDocRef.set(classroomUpdateDiff, { merge: true })
    ]);
  }

  public async classroomTokenExists(token: string): Promise<boolean> {
    const docReference = this.tokenDoc(token);
    const doc = await docReference.get();

    return doc.exists;
  }

  public async createClassroom(
    user: ITSUser,
    teacherClassroom: TeacherClassroom
  ) {
    const { token } = teacherClassroom;
    const { uid } = user;

    const tokenDocReference = this.tokenDoc(token);
    const teacherClassroomRef = this.teacherClassroom(token);
    const userRef = this.user(uid);

    this.db.runTransaction(async (transaction) => {
      const tokenDocument = await transaction.get(tokenDocReference);

      const exists = tokenDocument.exists;
      if (exists) {
        throw new DocumentExistsError(`Classroom ${token} already exists.`);
      }

      // add class to classes collection
      transaction.set(teacherClassroomRef, teacherClassroom, { merge: false });

      user.teacherClasses = { ...user.teacherClasses };
      const classroom: Classroom = {
        token: teacherClassroom.token,
        name: teacherClassroom.name,
      };

      // add class to user classes array
      user.teacherClasses[token] = classroom;
      user.classrooms[token] = classroom;
      transaction.set(userRef, user, { merge: false });

      // add token to token collection
      const tokenDoc: TokenDocument = { token, type: TOKEN_TYPE.CLASSROOM };
      transaction.set(tokenDocReference, tokenDoc);
    });
  }

  public async updateTeacherClassroom(
    teacherClassroom: TeacherClassroom
  ) {
    const teacherClassroomRef = this.teacherClassroom(teacherClassroom.token);
    teacherClassroomRef.set(teacherClassroom, { merge: false });
  }

  public async updateClassStartAndEndDates(classTokenId: string, startTsMs: number, endTsMs: number) {
    this.updateClassroomDoc(classTokenId, {startTsMs, endTsMs});
  }

  public async updateClassAutoGrade(classTokenId: string, sendGrades: boolean) {
    this.updateClassroomDoc(classTokenId, { sendGrades })
  }

  public async addInstructorNameToClass(classTokenId: string, instructorName: string) {
    if (!instructorName) {
      return;
    }

    try {
      const teacherClassroomRef = this.teacherClassroom(classTokenId);
      const classroomData = (await teacherClassroomRef.get()).data() as TeacherClassroom;
      // Instructor name already part of doc, ignore re-adding it
      if (classroomData.instructors && classroomData.instructors.indexOf(instructorName) >= 0) {
        return;
      }
      
      const instructorList = classroomData.instructors ? classroomData.instructors.concat() : [];
      instructorList.push(instructorName);
      this.updateClassroomDoc(classTokenId, { instructors: instructorList });
    } catch(e) {}
  }

  private async updateClassroomDoc(classTokenId: string, newData: Partial<TeacherClassroom>) {
    const teacherClassroomRef = this.teacherClassroom(classTokenId);
    teacherClassroomRef.set(newData, { merge: true });
  }

  public async getTeacherClassroomWithDefaults(classTokenId: string): Promise<TeacherClassroom> {
    const teacherClassroomRef = this.teacherClassroom(classTokenId);
    const classroomData = (await teacherClassroomRef.get()).data() as TeacherClassroom;

    let doUpdate = false;
    const propsToUpdate: Partial<TeacherClassroom> = {};
    if (classroomData.endTsMs === undefined) {
      doUpdate = true;
      propsToUpdate.endTsMs = Date.now();
    }
    if (classroomData.startTsMs === undefined) {
      doUpdate = true;
      propsToUpdate.startTsMs = Date.now();
    }
    if (classroomData.sendGrades === undefined) {
      doUpdate = true;
      propsToUpdate.sendGrades = true;
    }

    // If any missing/unset values, update the document with defaults
    if (doUpdate) {
      teacherClassroomRef.set(propsToUpdate, { merge: true });
    }

    return { ...classroomData, ...propsToUpdate };
  }

  public async getAssignments(classroomToken: string): Promise<ActiveLessons> {
    try {
      const ret = await this.getAssignmentsFn({ classroomToken });
      return ret.data.lessons;
    } catch (error) {
      console.log(error);
      return {};
    }
  }

  public async addStudentToClass(uid: string, token: string) {
    try {
      this.addStudentToClassFn({ uid, token });
    } catch (error) {
      console.log(error);
    }
  }

  public async updateDashboardTelemetry(uid: string, telemetryEnabled: boolean) {
    try {
      const userRef = this.user(uid);
      const userDoc = await userRef.get();
      if (userDoc.exists) {
        userRef.set({ telemetry: telemetryEnabled }, { merge: true });
      }
    } catch (error) {
      console.log(error);
    }
  }

  public user(
    uid: string
  ): firebaseApp.firestore.DocumentReference<firebaseApp.firestore.DocumentData> {
    return this.db.doc(`users/${uid}`);
  }

  public users(): firebaseApp.firestore.CollectionReference<firebaseApp.firestore.DocumentData> {
    return this.db.collection('users');
  }

  public teacherClassroom(
    token: string
  ): firebaseApp.firestore.DocumentReference<firebaseApp.firestore.DocumentData> {
    return this.db.doc(`teacherClassrooms/${token}`);
  }

  public teacherClassrooms(): firebaseApp.firestore.CollectionReference<firebaseApp.firestore.DocumentData> {
    return this.db.collection('teacherClassrooms');
  }

  public tokenDoc(
    token: string
  ): firebaseApp.firestore.DocumentReference<firebaseApp.firestore.DocumentData> {
    return this.db.doc(`tokens/${token}`);
  }

  public tokenDocs(): firebaseApp.firestore.CollectionReference<firebaseApp.firestore.DocumentData> {
    return this.db.collection('tokens');
  }

  /**
   * Get all issue cards matching a given parameters within a class.
   * For any particular screen snapshot of the teacher dashboard these are the cards displayed
   * 
   * @param classCode 
   * @param skill the module
   * @param cardType the category of the cards
   * @param version if teacher reset progress for a module, get only information past that reset point
   */
  public async getCardsByTypeAndSkill(
    classCode: string,
    skill: string | undefined,
    cardType: FirebaseCardType | undefined,
    version: string | undefined
  ): Promise<FirebaseRawCardData[]> {
    let collection: firebaseApp.firestore.CollectionReference | firebaseApp.firestore.Query = await this.db.collection('aggregateStats')
      .doc(classCode)
      .collection('cards');
    if (skill) {
      collection = collection.where('skill', '==', skill);
    }

    if (cardType) {
      collection = collection.where('type', '==', cardType);
    }

    if (version) {
      collection = collection.where('version', '==', version);
    }

    const results = await collection.get();
    const rawFirebaseData = results.docs.map((value) => ({
        id: value.id,
        eventIdToUser: {},
        ...value.data()
      } as FirebaseRawCardData
    ));
    const cardIdToRawData: Record<string, FirebaseRawCardData> = {};
    rawFirebaseData.forEach((value) => {
      cardIdToRawData[value.id] = value;
    });

    // There is a separate document per user+skill with list of that user event details per card.
    // Append this list into the data for each card.
    // NOTE: We don't bother filtering by version. The resulting document will contain information for all versions,
    // however, since the cardid has the version baked in as part of the hashed id they will naturally be filtered out
    // when we try to match cardids in the check further below.
    let userEventsCollection: firebaseApp.firestore.CollectionReference | firebaseApp.firestore.Query = await this.db.collection('aggregateStats')
        .doc(classCode)
        .collection('userEventsToCards');
    if (skill) {
      userEventsCollection = userEventsCollection.where('skill', '==', skill);
    }

    const userEventsResults = await userEventsCollection.get();
    userEventsResults.docs.forEach((value) => {
      const { cardIdToEvents } = value.data() as FirebaseUserSkillCardEvents;
      for (const cardId in cardIdToEvents) {
        // If card id exists in the card list, append all the user event details to the card document
        if (cardIdToRawData[cardId]) {
          const eventIdToDetailsMap = cardIdToEvents[cardId];
          for (const eventId in eventIdToDetailsMap) {
            cardIdToRawData[cardId].eventIdToUser[eventId] = eventIdToDetailsMap[eventId]
          }
        } 
      }
    });

    return rawFirebaseData;
  }

  /**
   * Update data in the cards.
   * Placeholder logic that should be moved to firebase function in final revision
   * 
   * @param version optional extra flag, if defined cardId hash should take into account the version. So all students in the same version will write to same cards.
   * Version should be included as a field in the general card doc too.
   */
  private async writeCards(
    classCode: string,
    skill: Skill,
    version: string | undefined,
    eventId: string,
    eventData: RawPageData,
  ) {
    // TODO: Include version as part of card id

    // While generating the card ids, include with it details for that card
    let cards: CardStat[] = [];
    const issueCardCallback = (
      key: string,
      tagId: string,
      questionType: string,
      userId: string) => {
      cards.push({
        cardId: this.simpleHash(key),
        cardType: 'Issue',
        details: {
          tagId: tagId,
          questionType
        },
        userInstanceDetails: {
          correct: false,
          uid: userId,
          problemLevel: eventData.playStats?.level,
          ts: Date.now()
        }
      });
    };

    const confusionCardCallback = (
      key: string, 
      tagAId: string, 
      tagBId: string,
      questionType: string,
      userId: string,
      correct: boolean
    ) => {
      cards.push({
        cardId: this.simpleHash(key),
        cardType: 'Confusion',
        details: {
          tagAId,
          tagBId
        },
        userInstanceDetails: {
          correct,
          uid: userId,
          problemLevel: eventData.playStats?.level,
          questionType,
          ts: Date.now()
        }
      })
    };

    const evalCardCallback = (
      key: string,
      questionType: string,
      userId: string,
      correct: boolean
    ) => {
      cards.push({
        cardId: this.simpleHash(key),
        cardType: 'Evaluation',
        details: {
          questionType
        },
        userInstanceDetails: {
          correct,
          uid: userId,
          ts: Date.now()
        }
      });
    };

    // Generate all the cards from the events
    processEvent(eventData, version, issueCardCallback, confusionCardCallback, evalCardCallback);

    // Remove duplicate hash keys
    cards = cards.filter((card, idx, arr) => arr.indexOf(card) === idx);

    // This block below will create the document for the general details of card.
    // The association between this general card and the user details as it relates to the issue/confusion of that
    // general card is stored in a separate document. This is to prevent race conditions where multiple students
    // are potentially trying to write to the exact same document.
    const skillId = skill.id;
    if (cards.length > 0) {
      for (const card of cards) {
        const cardRef = this.db.collection('aggregateStats')
          .doc(classCode)
          .collection('cards')
          .doc(card.cardId);
        // Need to handle edge case where name hash collides.
        // To properly check this
        // To resolve just reapply the hash again.
        const snapshot = await cardRef.get();
        // For each tagKey that doesn't set have a document, create a new one
        if (!snapshot.exists) {
          await cardRef.set({
            skill: skillId,
            type: card.cardType, // Either issue or confusion
            details: card.details,  // Tag specific information
          });
        }
      }

      // Save/update event details for this user + skill combination
      // This is document belongs to only the current user, have this to prevent write conflicts as only
      // the current user only ever appends events to this doc instead of all user updating the
      // card document you see above.
      const dataToUpdate: Record<string, any> = {};
      for (const card of cards) {
        dataToUpdate[card.cardId] = { [eventId]: card.userInstanceDetails };
      }
      const userId = eventData.playStats?.userId;
      const userEventsRef = this.db.collection('aggregateStats')
        .doc(classCode)
        .collection('userEventsToCards')
        .where('skill', '==', skillId)
        .where('userId', '==', userId).limit(1);
      const userEventsSnapshot = await userEventsRef.get();
      if (userEventsSnapshot.empty) {
        this.db.collection('aggregateStats').doc(classCode).collection('userEventsToCards').add({
          userId,
          skill: skillId,
          cardIdToEvents: dataToUpdate
        })
      } else {
        userEventsSnapshot.docs[0].ref.set({ cardIdToEvents: dataToUpdate }, { merge: true })
      }
    }
  }

  /**
   * Get a specific logged question example. Used to display that specific example in the dashboard.
   * 
   * @param eventDocId 
   * @param classCode 
   * @returns 
   */
  public async getSpecificUserEvent(
    eventDocId: string,
    classCode: string
  ): Promise<StudentResult> {
    const result = await this.db.collection('events')
      .doc(classCode)
      .collection('submissions')
      .doc(eventDocId)
      .get();
    return { id: result.id, data: JSON.parse(result.get('details'))};
  }

  /**
   * 
   * @param skill if undefined, fetch all events
   * @param classCode 
   * @returns 
   */
  public async getAllEventsForSkill(skill: string | undefined, classCode: string): Promise<StudentResult[]> {
    const resultPayload = (skill) ?
      await this.db.collection('events')
        .doc(classCode)
        .collection('submissions')
        .where('skill', '==', skill)
        .get() :
      await this.db.collection('events')
        .doc(classCode)
        .collection('submissions')
        .get();

    return resultPayload.docs.map(doc => ({ id: doc.id, data: JSON.parse(doc.get('details'))}));
  }

  /**
   * Get all logged questions for a specific user and skill/module.
   * Used to populate the student problem row.
   * 
   * @param userId 
   * @param skill 
   * @param classCode 
   */
  public async getAllEventsForUserInSkill(userId: string, skill: string, classCode: string): Promise<StudentResult[]> {
    const resultPayload = await this.db.collection('events')
      .doc(classCode)
      .collection('submissions')
      .where('userId', '==', userId)
      .where('skill', '==', skill)
      .get();
    return resultPayload.docs.map(doc => ({ id: doc.id, data: JSON.parse(doc.get('details'))}));
  }

  /**
   * Write out a single user question response event and update associated cards with it.
   * 
   * @param userId
   * @param classCode
   * @param version 
   * @param eventData 
   * @param time 
   * @returns 
   */
  public async addUserEvent(
    userId: string,
    classCode: string,
    version: string | undefined,
    eventData: RawPageData,
    time: number
  ) {
    let userEventData = {
      userId,
      skill: eventData.skill.id,
      details: JSON.stringify(eventData), // Make sure this has an index exemption
      ts: time,
      version
    };
    if (!version) {
      delete userEventData.version;
    }
    const createdDocument = await this.db
      .collection('events')
      .doc(classCode)
      .collection('submissions')
      .add(userEventData);

    // Use event data to write out card information
    this.writeCards(classCode, eventData.skill, version, createdDocument.id, eventData);
    return createdDocument.id;
  }

  /**
   * Load all dashboard related settings in one batch
   * 
   * @param classCode 
   * @returns 
   */
  public async getCardSettings(classCode: string): Promise<DashboardLoadPayload> {
    const cardIndividualSettings = await this.getDashboardSettingsDoc(classCode, 'cards').get();
    const cardDisplaySettings = await this.getDashboardSettingsDoc(classCode, 'cardDisplay').get();

    const payload = {
      cards: cardIndividualSettings.exists ? cardIndividualSettings.data() as CardIdToSelectionMap : {},
      cardSettings: cardDisplaySettings.exists ? cardDisplaySettings.data() as CardSettingsPayload : {
        minUsers: 3,
        minPercent: 5
      }
    };
    return payload;
  }

  /**
   * 
   * @param classCode 
   * @param cardData 
   * @param docName
   *    'cards' for individual card generation settings or 'cardDisplay' for general dashboard display of
   *    cards
   */
  public async saveCardSettings(classCode: string, cardData: object, docName: CARD_DOC_TYPES) {
    await this.getDashboardSettingsDoc(classCode, docName).set(cardData, { merge: true });
  }

  private getDashboardSettingsDoc(classCode: string, docName: string) {
    return this.db.collection('teacherClassrooms')
      .doc(classCode)
      .collection('dashboardSettings')
      .doc(docName);
  }

  /**
   * We want to keep track of all self-eval type responses and store them in a special collection
   * so they can be verified later.
   */
  public async saveSelfEvalResponses(eventId: string, rawData: RawEvalPageData, classCode: string) {
    const problemId = rawData.id;
    const responses = [rawData.firstResponse];
    if (rawData.firstResponse != rawData.secondResponse) {
      responses.push(rawData.secondResponse);
    }

    for (const response of responses) {
      if (response && response.length > 0) {
        this.db.collection('selfEvalResponses').add({
          eventId,
          classCode,
          skill: rawData.skill.id,
          selfEvalProblemId: problemId,
          response: response,
          status: 'open',
          timestamp: Date.now()
        });
      }
    }
  }

  public async getSelfEvalResponses(limit = -1, status?: SelfEvalStatus) {
    let ref: firebaseApp.firestore.CollectionReference | firebaseApp.firestore.Query = this.db.collection('selfEvalResponses');
    if (status) {
      ref= ref.where('status', '==', status);
    }
    if (limit > 0) {
      ref = ref.limit(limit);
    }
      
    const evalResponses = await ref.get();
    return evalResponses.docs.map((value) => {
      return {
        id: value.id,
        ...value.data()
      } as SelfEvalResponse;
    });
  }

  public async updateSelfEvalResponse(docId: string, newStatus: string) {
    this.db.collection('selfEvalResponses').doc(docId).set({ status: newStatus }, {merge: true});
  }

  public async getBugReports(limit: number) {
    const bugReports = await this.db.collection('bugReports')
      .where('status', '==', BugReportStatus.Open)
      .limit(limit)
      .get();
    return bugReports.docs.map((value) => {
      return {
        id: value.id,
        ...value.data()
      } as BugReport;
    });
  }

  public async updateBugReportStatus(bugId: string, status: BugReportStatus) {
    const bugReportRef = this.db.collection('bugReports').doc(bugId);
    await bugReportRef.set({ status }, { merge: true });
  }

  /**
   * TODO: Move to util function
   * 
   * @param input 
   * @returns 
   */
  public simpleHash (input: string) {
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash &= hash; // Convert to 32bit integer
    }
    return new Uint32Array([hash])[0].toString(36).padStart(12, '0');
  };
}

type CARD_DOC_TYPES = 'cards' | 'cardDisplay';

export const FIREBASE = new Firebase();